import { useEffect, useReducer } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import Button from 'components/new-ui/base/button/Button';
import CardWithIcon from 'components/new-ui/base/card/CardWithIcon';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import Segmented from 'components/new-ui/base/segmented/Segmented';
import AdditionalChargesTable from '../components/AdditionalChargesTable';
import AdditionalChargesModal from '../components/AdditionalChargesModal';
import {
    archiveAdditionalCharge,
    getCustomersUsingAdditionalCharge,
    unarchiveAdditionalCharge,
} from 'store/invoice-config/client';
import ArchiveChargeModal from '../components/ArchiveChargeModal';
import UnarchiveChargeModal from '../components/UnarchiveChargeModal';
import useInvoiceChargesParams from '../../hooks/useInvoiceChargesParams';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useInvoiceChargesFilters from '../../hooks/useInvoiceChargesFilters';
import { AdditionalCharge } from 'store/invoice-config/types';
import { getCurrencyData } from 'utils/common';
import useInvoicesConfig from 'hooks/useInvoicesConfig';

type ModalType = 'none' | 'create' | 'edit' | 'archive' | 'unarchive';

interface ModalState {
    type: ModalType;
    charge?: AdditionalCharge;
    customersUsingCount?: number;
}

type ModalAction =
    | { type: 'OPEN_CREATE' }
    | { type: 'OPEN_EDIT'; charge: AdditionalCharge }
    | { type: 'OPEN_ARCHIVE'; charge: AdditionalCharge; customersUsingCount: number }
    | { type: 'OPEN_UNARCHIVE'; charge: AdditionalCharge }
    | { type: 'CLOSE' };

const modalReducer = (_: ModalState, action: ModalAction): ModalState => {
    switch (action.type) {
        case 'OPEN_CREATE':
            return { type: 'create' };

        case 'OPEN_EDIT':
            return { type: 'edit', charge: action.charge };

        case 'OPEN_ARCHIVE':
            return { type: 'archive', charge: action.charge, customersUsingCount: action.customersUsingCount };

        case 'OPEN_UNARCHIVE':
            return { type: 'unarchive', charge: action.charge };

        case 'CLOSE':
            return { type: 'none' };

        default:
            return { type: 'none' };
    }
};

const AdditionalCharges = () => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    useInvoiceChargesParams();
    const { config } = useInvoicesConfig();

    const [modal, dispatch] = useReducer(modalReducer, { type: 'none' });

    const {
        data,
        refetch,
        isLoading,
        handleTableChange,
        pagination,
        appliedParams,
        queryInputVal,
        setArchived,
        setQueryInputVal,
    } = useInvoiceChargesFilters();

    const { archived, sort, sortDirection } = appliedParams;

    const { currencySymbol } = getCurrencyData();

    const closeModal = () => dispatch({ type: 'CLOSE' });

    const handleOnSubmit = () => {
        closeModal();
        refetch();
    };

    const handleArchive = async () => {
        const chargeId = modal.charge?.id;

        try {
            if (!chargeId) throw new Error('Missing additional charge id');

            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: {
                    message: 'Archiving additional charge',
                },
            });

            await archiveAdditionalCharge(chargeId);

            await refetch();
            closeModal();

            Notification({
                type: 'success',
                messageTitle: 'Operation Successful',
                description: 'Additional charge archived successfully.',
                placement: 'top',
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error);

            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errorMessage || 'Could not archive charge. Please try again.',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    // Check if the additional charge is in use by some customers and show a confirmation message in such a case
    const handleOpenArchive = async (charge: AdditionalCharge) => {
        try {
            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: {
                    message: 'Checking additional charge usage',
                },
            });

            const customerIdsUsingTheCharge = await getCustomersUsingAdditionalCharge(charge.id);

            dispatch({ type: 'OPEN_ARCHIVE', charge, customersUsingCount: customerIdsUsingTheCharge.length });
        } catch (error) {
            const errorMessage = getErrorMessage(error);

            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errorMessage || 'Could not verify charge usage. Please try again.',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleUnarchive = async () => {
        const chargeId = modal.charge?.id;

        try {
            if (!chargeId) throw new Error('Missing additional charge id');

            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: {
                    message: 'Unarchiving additional charge',
                },
            });

            await unarchiveAdditionalCharge(chargeId);

            await refetch();
            closeModal();

            Notification({
                type: 'success',
                messageTitle: 'Operation Successful',
                description: 'Additional charge unarchived successfully.',
                placement: 'top',
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error);

            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errorMessage || 'Could not unarchive charge. Please try again.',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    useEffect(() => {
        hideGlobalOverlay();
    }, [hideGlobalOverlay]);

    return (
        <>
            <CardWithIcon
                gap="large"
                iconProps={{
                    icon: <PlusOutlined />,
                    status: 'warning',
                }}
                layout="horizontal"
                title="Additional Charges"
                subtitle="Create and manage additional charges that can be manually applied to customer invoices."
                headerActions={
                    <>
                        <Space size={8}>
                            <Segmented
                                value={archived ? 'archived' : 'active'}
                                onChange={(val) => setArchived(val === 'active' ? false : true)}
                                options={[
                                    {
                                        value: 'active',
                                        label: 'Active',
                                    },
                                    {
                                        value: 'archived',
                                        label: 'Archived',
                                    },
                                ]}
                            />

                            <Button
                                onClick={() => {
                                    dispatch({ type: 'OPEN_CREATE' });
                                }}
                            >
                                Create New
                            </Button>
                        </Space>
                    </>
                }
                children={
                    <Space direction="vertical" size={16} style={{ marginTop: 16 }}>
                        <SearchBar
                            inputPlaceholder="Search"
                            value={queryInputVal}
                            onSearch={(val) => setQueryInputVal(val)}
                            onClear={() => setQueryInputVal('')}
                        />
                        <AdditionalChargesTable
                            currencySymbol={currencySymbol}
                            showActions
                            actions={{
                                onArchive: handleOpenArchive,
                                onUnarchive: (record) => dispatch({ type: 'OPEN_UNARCHIVE', charge: record }),
                            }}
                            onChange={handleTableChange}
                            currentSortDirection={sortDirection}
                            currentSortParam={sort}
                            loading={isLoading}
                            data={data?.list}
                            pagination={pagination}
                            configuredAdditionalChargeId={config?.additionalCharge?.id}
                        />
                    </Space>
                }
            />

            <AdditionalChargesModal
                currencySymbol={currencySymbol}
                open={modal.type === 'create' || modal.type === 'edit'}
                selectedCharge={modal.type === 'edit' ? modal.charge : undefined}
                onClose={closeModal}
                onSubmit={handleOnSubmit}
            />

            <ArchiveChargeModal
                open={modal.type === 'archive'}
                customersUsingCount={modal.customersUsingCount}
                onConfirm={handleArchive}
                onClose={closeModal}
            />
            <UnarchiveChargeModal open={modal.type === 'unarchive'} onConfirm={handleUnarchive} onClose={closeModal} />
        </>
    );
};

export default AdditionalCharges;
